import React from 'react';
import { Link } from 'react-router-dom';
import type { BlogSection } from '../data/blogPosts';

export function renderBlogSection(section: BlogSection, index: number): React.ReactNode {
  switch (section.type) {
    case 'h2':
      return (
        <h2 key={index} className="text-2xl font-bold text-slate-900 mt-10 mb-4">
          {section.text}
        </h2>
      );

    case 'h3':
      return (
        <h3 key={index} className="text-xl font-semibold text-slate-800 mt-6 mb-3">
          {section.text}
        </h3>
      );

    case 'p':
      return (
        <p key={index} className="text-gray-700 leading-relaxed mb-4">
          {section.text}
        </p>
      );

    case 'ul':
      return (
        <ul key={index} className="my-4 space-y-2 pl-1">
          {(section.items ?? []).map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-gray-700">
              <span className="mt-1 shrink-0 w-1.5 h-1.5 rounded-full bg-orange-500 mt-2" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );

    case 'ol':
      return (
        <ol key={index} className="my-4 space-y-2 pl-5 list-decimal text-gray-700">
          {(section.items ?? []).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      );

    case 'quote':
      return (
        <blockquote
          key={index}
          className="my-6 pl-6 border-l-4 border-orange-400 bg-orange-50 rounded-r-xl py-4 pr-4"
        >
          <p className="text-slate-700 italic">{section.text}</p>
        </blockquote>
      );

    case 'tip':
      return (
        <div
          key={index}
          className="my-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3"
        >
          <span className="text-xl shrink-0">💡</span>
          <p className="text-amber-900 text-sm">{section.text}</p>
        </div>
      );

    case 'table':
      return (
        <div key={index} className="my-6 overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {(section.headers ?? []).map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-left font-semibold text-slate-700 border-b border-gray-200"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(section.rows ?? []).map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-3 text-gray-700 border-b border-gray-100">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'cta':
      return (
        <div
          key={index}
          className="my-10 p-8 bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl text-center"
        >
          <p className="text-white font-semibold text-xl mb-4">{section.text}</p>
          <Link
            to={section.href ?? '/register'}
            className="inline-block px-8 py-3 bg-white text-orange-600 font-bold rounded-xl hover:bg-orange-50 transition-colors"
          >
            {section.label ?? 'Get Started Free →'}
          </Link>
        </div>
      );

    default:
      return null;
  }
}

interface BlogContentRendererProps {
  sections: BlogSection[];
}

const BlogContentRenderer: React.FC<BlogContentRendererProps> = ({ sections }) => (
  <>{sections.map((section, index) => renderBlogSection(section, index))}</>
);

export default BlogContentRenderer;
